/* @flow */

//----------------------------------------------------------------------//
//                         Imported NPM modules                         //
//----------------------------------------------------------------------//

import * as React from 'react';
import { observer } from 'mobx-react';
import { withStyles } from '@material-ui/core/styles';

//----------------------------------------------------------------------//
//                     Imported app modules/assets                      //
//----------------------------------------------------------------------//
import Button from '@material-ui/core/Button';
import IconButton from '@material-ui/core/IconButton';
import Tabs from '@material-ui/core/Tabs';
import TextField from '@material-ui/core/TextField';
import Tooltip from '@material-ui/core/Tooltip';
import Zoom from '@material-ui/core/Zoom';

import styles from './customized.module.scss';

//----------------------------------------------------------------------//
//                       Exported React components                      //
//----------------------------------------------------------------------//

// eslint-disable-next-line flow-prefer-exact-object-types, flowtype/no-weak-types
type TypePropsBoundInput = { model: Object, prop: string };
type TypeBoundTextFieldProps = TypePropsBoundInput & {
  multiline: boolean,
  className: string,
  hintText: string,
  floatingLabelText: string,
  type: string,
  endAdornment?: React.Element<*>,
  required?: boolean,
};

export const BoundTextField: (props: TypeBoundTextFieldProps) => React.Element<*> = observer(
  (props: TypeBoundTextFieldProps): React.Element<*> => (
    <TextField
      margin="normal"
      label={props.floatingLabelText}
      placeholder={props.hintText}
      onChange={(e: SyntheticInputEvent<*>) => {
        props.model[props.prop] = e.target.value;
      }}
      value={props.model[props.prop] || ''}
      autoComplete="on"
      className={props.className}
      multiline={props.multiline}
      rows={props.multiline && props.rows}
      type={props.type}
      helperText={props.helperText}
      required={props.required}
    />
  )
);

export const SearchTextField: (props: *) => React.Element<*> = observer(
  (props: *): React.Element<*> => {
    const { onClear, endAdornment, disabled, ...other } = props;

    function clearButton() {
      if (props.value.length > 0 && typeof onClear === 'function') {
        return (
          <IconButton
            className={`${styles['clear-button']}${disabled ? ` ${styles.disabled}` : ''}`}
            onClick={onClear}
          >
            <svg>
              <use xlinkHref="#icon-declined" />
            </svg>
          </IconButton>
        );
      }

      return null;
    }

    return (
      <div
        className={
          props.variant === 'primary'
            ? styles['search-text-field-primary']
            : styles['search-text-field-secondary']
        }
      >
        <svg className={disabled ? styles.disabled : undefined}>
          <use xlinkHref="#icon-search" />
        </svg>
        <TextField
          className={styles['search-bar']}
          fullWidth={true}
          InputProps={{
            disableUnderline: true,
            endAdornment: endAdornment || clearButton(),
          }}
          disabled={disabled}
          {...other}
        />
      </div>
    );
  }
);

export const ElectroTabs: () => React.Element<*> = observer(
  (props): React.Element<*> => {
    return (
      <Tabs className={styles['elr-tabs']} {...props}>
        {props.children}
      </Tabs>
    );
  }
);

export const ElectroButton: () => React.Element<*> = observer(
  (props: *): React.Element<*> => (
    <Button
      variant="contained"
      className={props.className ? props.className : 'elr-button'}
      {...props}
    >
      {props.label}
    </Button>
  )
);

const tooltipStyles = (theme: Object) => ({
  arrowWrapper: {},
  tooltipText: {},
  arrowPopper: {
    '&[x-placement*="bottom"] $arrowArrow': {
      top: 0,
      left: 0,
      marginTop: '-0.9em',
      width: '3em',
      height: '1em',
      '&::before': {
        borderWidth: '0 1em 1em 1em',
        borderColor: 'transparent transparent #2A3338 transparent',
      },
    },
  },
  arrowArrow: {
    position: 'absolute',
    fontSize: 7,
    width: '3em',
    height: '3em',
    '&::before': {
      content: '""',
      margin: 'auto',
      display: 'block',
      width: 0,
      height: 0,
      borderStyle: 'solid',
    },
  },
});

export type ElectroTooltipProps = {
  title: string,
  classes?: { [string]: string },
  children: React.Node,
  disabled?: boolean,
};

@withStyles(tooltipStyles)
export class ElectroTooltip extends React.Component<
  ElectroTooltipProps,
  { arrowRef: React.Ref<any> | null }
> {
  state = {
    arrowRef: null,
  };

  handleArrowRef = (node: any) => {
    this.setState({
      arrowRef: node,
    });
  };

  render(): React.Element<*> {
    const { classes = {}, disabled } = this.props;

    return (
      <Tooltip
        classes={{ popper: classes.arrowPopper }}
        TransitionComponent={Zoom}
        disableFocusListener={true}
        open={disabled ? false : undefined}
        title={
          <div className={classes.arrowWrapper} style={{ textAlign: 'center' }}>
            <span className={classes.arrowArrow} ref={this.handleArrowRef} />
            <span
              className={classes.tooltipText}
              dangerouslySetInnerHTML={{ __html: this.props.title }}
            />
          </div>
        }
        PopperProps={{
          popperOptions: {
            modifiers: {
              arrow: {
                enabled: Boolean(this.state.arrowRef),
                element: this.state.arrowRef,
              },
            },
          },
        }}
      >
        {this.props.children}
      </Tooltip>
    );
  }
}
